import React, { useCallback, useEffect, useState } from 'react';
import { Plus, Search, Save, Check, CircleMinus, CircleCheck, MapPinned, Edit2, Lock } from 'lucide-react';
import { geographyTypeService, GeographyType } from '../../services/geographyTypeService';
import FormControl from '../common/FormControl';
import Button from '../common/Button';
import Modal from '../common/Modal';
import Toast, { ToastMessage } from '../common/Toast';
import { confirmWarning, confirmActive } from '../common/confirm';
import extractApiError from '../../utils/apiError';
import { useFieldValidation } from '../../validation/useFieldValidation';
import { ValidatedInput, ValidatedTextarea } from '../../validation/ValidatedFields';
import { isUniqueLocal } from '../../validation/uniqueness';
import { usePermission, NOT_AUTHORISED_MSG } from '../../contexts/PermissionContext';
import NoDataFound from '../common/NoDataFound';
import StatusSwitch from '../common/StatusSwitch';
import ApprovalDot from '../common/ApprovalDot';
import WorkflowModal from '../common/WorkflowModal';
import Pagination from '../common/Pagination';

const GeographyTypesMaster: React.FC = () => {
    const { allowed: canView, loading: viewLoading } = usePermission('ORG_GEOGRAPHIES', 'view');
    const { allowed: canModify, loading: permLoading } = usePermission('ORG_GEOGRAPHIES', 'view+modify');
    const fieldVh = useFieldValidation();
    const [items, setItems] = useState<GeographyType[]>([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState<'ACTIVE' | 'INACTIVE'>('ACTIVE');
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalRecords, setTotalRecords] = useState(0);

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editing, setEditing] = useState<GeographyType | null>(null);
    // View-first edit: existing records open read-only; the "Edit" button in the
    // footer switches the form to editable.
    const [isEditing, setIsEditing] = useState(false);
    const [formData, setFormData] = useState<{ name: string; description: string }>({ name: '', description: '' });
    const [submitting, setSubmitting] = useState(false);
    const [toggling, setToggling] = useState(false);

    const [toast, setToast] = useState<ToastMessage | null>(null);
    const [workflowOpenFor, setWorkflowOpenFor] = useState<GeographyType | null>(null);

    // Reset to page 1 whenever the result set changes shape
    useEffect(() => { setPage(1); }, [searchTerm, statusFilter, pageSize]);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const resp = await geographyTypeService.list({
                status: statusFilter,
                search: searchTerm || undefined,
                limit: pageSize,
                page: page,
            });
            const data = resp.data || [];
            const sorted = [...data].sort((a, b) => (b.id || 0) - (a.id || 0));
            setItems(sorted);
            setTotalRecords((resp as any).total || data.length);
        } catch (err: any) {
            setToast({ type: 'error', message: err?.message || 'Failed to load geography types.' });
        } finally {
            setLoading(false);
        }
    }, [statusFilter, searchTerm, page, pageSize]);

    useEffect(() => { if (canView) load(); }, [load, canView]);

    // Approved state — determines if form is locked
    const isApproved = editing?.workflow_state === 'approved';
    // Fields are read-only when viewing an existing record (before "Edit" clicked).
    const fieldsDisabled = !!editing && (!isEditing || !canModify || isApproved);

    const openCreate = () => {
        if (!canModify) return;
        setEditing(null);
        setIsEditing(true); // create is always editable
        setFormData({ name: '', description: '' });
        fieldVh.reset();
        setIsModalOpen(true);
    };

    // Open an existing record in read-only (view) mode; "Edit" enables the form.
    const openEdit = (gt: GeographyType) => {
        setEditing(gt);
        setIsEditing(false);
        setFormData({ name: gt.name, description: gt.description || '' });
        fieldVh.reset();
        setIsModalOpen(true);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (fieldsDisabled) return;
        const engineOk = fieldVh.validateAll([
            { name: 'name', code: 109, value: formData.name, required: true },
            { name: 'description', code: 110, value: formData.description },
        ]);
        if (!engineOk) return;
        if (!isUniqueLocal(916, formData.name, items, editing?.id)) {
            fieldVh.setError('name', 'A geography type with this name already exists.');
            return;
        }
        setSubmitting(true);
        try {
            if (editing) {
                await geographyTypeService.update(editing.id, {
                    name: formData.name.trim(),
                    description: formData.description.trim(),
                });
                setToast({ type: 'success', message: 'Geography type updated.' });
            } else {
                await geographyTypeService.create({
                    name: formData.name.trim(),
                    description: formData.description.trim() || undefined,
                });
                setToast({ type: 'success', message: 'Geography type created.' });
            }
            setIsModalOpen(false);
            await load();
        } catch (err: any) {
            const msg = extractApiError(err, 'Failed to save geography type.');
            if (/exist|duplicate|unique/i.test(msg)) {
                fieldVh.setError('name', msg);
            } else {
                setToast({ type: 'error', message: msg });
            }
        } finally {
            setSubmitting(false);
        }
    };

    const handleToggleActive = async () => {
        if (!editing || !canModify) return;
        const next = editing.is_active === 1 ? 0 : 1;
        const isActive = editing.is_active === 1;
        const confirmFn = isActive ? confirmWarning : confirmActive;
        const ok = await confirmFn({
            title: next === 1 ? 'Reactivate this geography type?' : 'Inactivate this geography type?',
            text: next === 1 ? 'It will be selectable on the Geography form again.' : 'It will be hidden from the Geography form picker.',
            confirmText: next === 1 ? 'Yes, Reactivate' : 'Yes, Inactivate',
        });
        if (!ok) return;
        setToggling(true);
        try {
            await geographyTypeService.update(editing.id, { is_active: next });
            setToast({ type: next === 1 ? 'success' : 'warning', message: next === 1 ? 'Reactivated.' : 'Inactivated.' });
            setIsModalOpen(false);
            await load();
        } catch (err: any) {
            setToast({ type: 'error', message: extractApiError(err, 'Failed to update status.') });
        } finally {
            setToggling(false);
        }
    };

    const closeWfModal = () => setWorkflowOpenFor(null);

    return (
        <div className="flex flex-col flex-1 min-w-0 h-full">
            {(viewLoading || canView) && (
                <div className="netbody-header">
                    <StatusSwitch
                        value={statusFilter === 'INACTIVE' ? 'INACTIVE' : 'ACTIVE'}
                        onChange={(next) => setStatusFilter(next)}
                        noun="geography types"
                        testId="SEL_geo_types_status"
                    />
                    <div className="flex items-center gap-2 ml-auto">
                        <FormControl.Input
                            type="text"
                            placeholder="Search geography types..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            leadingIcon={<Search size={14} />}
                            className="filter-search"
                            data-testid="INP_geo_types_search"
                        />
                        <Button
                            variant="fill"
                            icon={Plus}
                            onClick={openCreate}
                            disabled={permLoading || !canModify}
                            title={!canModify ? NOT_AUTHORISED_MSG : undefined}
                            data-testid="BTN_geo_types_create"
                        >Create</Button>
                    </div>
                </div>
            )}

            <div className="netbody-content">
                {!viewLoading && !canView ? (
                    <div style={{ padding: '40px 20px', textAlign: 'center' }}>
                        <NoDataFound title="Not authorised" message="You do not have access to 'ORG_GEOGRAPHIES'." />
                    </div>
                ) : (
                    <div className="scroll-container flex-1 overflow-hidden">
                        <div className="table-list" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                            <div className="table-list-scroll" style={!loading && items.length === 0 ? { flex: '0 0 auto', display: 'none' } : undefined}>
                                <table className="table-list-table">
                                    <thead>
                                        <tr>
                                            <th className="table-list-th table-list-cell--dot" aria-label="Workflow status"></th>
                                            <th className="table-list-th table-list-cell--sno">S.No</th>
                                            <th className="table-list-th" style={{ width: 90, minWidth: 90 }}>Code</th>
                                            <th className="table-list-th">Geography Type</th>
                                        </tr>
                                    </thead>
                                    <tbody aria-busy={loading} aria-live="polite">
                                        {loading && (
                                            <>
                                                {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => (
                                                    <tr key={i} className="table-list-skeleton-row">
                                                        <td className="table-list-cell table-list-cell--dot">
                                                            <div className="table-list-skeleton-bar skel-dot" />
                                                        </td>
                                                        <td className="table-list-cell table-list-cell--sno">
                                                            <div className="table-list-skeleton-bar table-list-skeleton-bar--w-2xs" />
                                                        </td>
                                                        <td className="table-list-cell">
                                                            <div className="table-list-skeleton-bar table-list-skeleton-bar--w-sm" />
                                                        </td>
                                                        <td className="table-list-cell">
                                                            <div className="table-list-skeleton-bar table-list-skeleton-bar--w-md" />
                                                        </td>
                                                    </tr>
                                                ))}
                                            </>
                                        )}
                                        {!loading && items.map((gt, idx) => (
                                            <tr key={gt.id} className={`table-list-row${gt.is_active === 1 ? '' : ' is-inactive'}`} onClick={() => openEdit(gt)} data-testid={`BTN_geo_types_row_${gt.id}_edit`}>
                                                <td
                                                    className="table-list-cell table-list-cell--dot"
                                                    onClick={e => { e.stopPropagation(); setWorkflowOpenFor(gt); }}
                                                    title="Workflow — view timeline & act"
                                                    data-testid={`BTN_geo_types_row_${gt.id}_workflow`}
                                                >
                                                    <ApprovalDot
                                                        state={(gt.workflow_state || 'draft') as any}
                                                        onClick={e => { e.stopPropagation(); setWorkflowOpenFor(gt); }}
                                                        title="Workflow & history"
                                                        data-testid={`BTN_geo_types_row_${gt.id}_wf`}
                                                    />
                                                </td>
                                                <td className="table-list-cell table-list-cell--sno">{idx + 1}</td>
                                                <td className="table-list-cell" style={{ width: 90, minWidth: 90 }} data-testid={`CELL_geo_types_code_${gt.id}`}>
                                                    <span className="cell-code">{gt.code ?? '—'}</span>
                                                </td>
                                                <td className="table-list-cell">
                                                    <span className="table-list-cell-title">
                                                        {(gt as any).workflow_state === 'approved' && (
                                                            <Lock className="lock-icon lock-icon--sm" aria-label="Locked — approved" />
                                                        )}
                                                        {gt.name}
                                                    </span>
                                                    {gt.description && (
                                                        <span className="table-list-cell-sub">{gt.description}</span>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            {!loading && items.length === 0 && (
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1, minHeight: '300px' }}>
                                    <NoDataFound title="No geography types defined yet." message="" />
                                </div>
                            )}
                            <Pagination
                                page={page}
                                pageSize={pageSize}
                                totalRecords={totalRecords}
                                onPageChange={setPage}
                                onPageSizeChange={setPageSize}
                                pageSizeOptions={[10, 20, 50, 100]}
                                recordLabel="geography types"
                            />
                        </div>
                    </div>
                )}
            </div>

            <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} size="md">
                <Modal.Header>
                    {editing
                        ? <>{isApproved ? 'Viewing (locked)' : (isEditing ? 'Editing' : 'Viewing')} "<span className="item-highlight">{editing.name}</span>"</>
                        : 'Create Geography Type'}
                </Modal.Header>
                <Modal.Body>
                    {isApproved && (
                        <div className="approved-banner">
                            <Lock size={14} /> Record is approved and locked. Reopen via the workflow button to edit.
                        </div>
                    )}
                    <form id="geo-type-form" onSubmit={handleSubmit} className="flex flex-col gap-y-4" data-testid="MOD_geo_types_form">
                        {/* System code (GEO-N) — auto-generated on approve, read-only.
                            Prefilled with the same code the list shows. */}
                        {editing && (
                            <FormControl.Input
                                label="Code"
                                value={(editing as any).code || '—'}
                                disabled
                                data-testid="INP_geo_types_code"
                            />
                        )}
                        <ValidatedInput
                            vh={fieldVh}
                            name="name"
                            code={109}
                            required
                            label="Name"
                            value={formData.name}
                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                            placeholder="e.g. Hub Office"
                            data-testid="INP_geo_types_name"
                            disabled={fieldsDisabled}
                        />
                        <ValidatedTextarea
                            vh={fieldVh}
                            name="description"
                            code={110}
                            label="Description (Optional)"
                            rows={3}
                            value={formData.description}
                            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                            placeholder="What kind of geography this represents..."
                            data-testid="INP_geo_types_description"
                            disabled={fieldsDisabled}
                        />
                    </form>
                </Modal.Body>
                <Modal.Footer className={editing ? 'app-modal-footer--split' : ''}>
                    {editing && !isApproved && (
                        <Button
                            variant={editing.is_active === 1 ? 'inactive-outline' : 'active-outline'}
                            icon={editing.is_active === 1 ? CircleMinus : CircleCheck}
                            onClick={handleToggleActive}
                            disabled={permLoading || !canModify || toggling || submitting}
                            title={!canModify ? NOT_AUTHORISED_MSG : undefined}
                            data-testid="BTN_geo_types_toggle_active"
                        >
                            {toggling ? '...' : editing.is_active === 1 ? 'Inactive' : 'Active'}
                        </Button>
                    )}
                    <div style={{ display: 'flex', gap: 8 }}>
                        {/* Create → Submit. */}
                        {!editing && (
                            <Button
                                type="submit"
                                form="geo-type-form"
                                variant="fill"
                                icon={Save}
                                disabled={permLoading || !canModify || submitting || toggling}
                                title={!canModify ? NOT_AUTHORISED_MSG : undefined}
                                data-testid="BTN_geo_types_submit"
                            >
                                {submitting ? 'Submitting...' : 'Submit'}
                            </Button>
                        )}
                        {/* View mode → "Edit" enables the form (but not if approved). */}
                        {editing && !isEditing && !isApproved && (
                            <Button
                                variant="fill"
                                icon={Edit2}
                                onClick={() => {
                                    if (permLoading || !canModify) { setToast({ type: 'error', message: NOT_AUTHORISED_MSG }); return; }
                                    setIsEditing(true);
                                }}
                                disabled={permLoading || !canModify}
                                title={!canModify ? NOT_AUTHORISED_MSG : undefined}
                                data-testid="BTN_geo_types_edit"
                            >
                                Edit
                            </Button>
                        )}
                        {/* Edit mode → "Update" saves (but not if approved). */}
                        {editing && isEditing && !isApproved && (
                            <Button
                                type="submit"
                                form="geo-type-form"
                                variant="update"
                                icon={Check}
                                disabled={permLoading || !canModify || submitting || toggling}
                                title={!canModify ? NOT_AUTHORISED_MSG : undefined}
                                data-testid="BTN_geo_types_submit"
                            >
                                {submitting ? 'Updating...' : 'Update'}
                            </Button>
                        )}
                    </div>
                </Modal.Footer>
            </Modal>

            <Toast toast={toast} onClose={() => setToast(null)} />

            <WorkflowModal
                isOpen={!!workflowOpenFor}
                onClose={closeWfModal}
                kind="org_geography_type"
                id={workflowOpenFor ? Number(workflowOpenFor.id) : null}
                onChanged={load}
            />
        </div>
    );
};

export default GeographyTypesMaster;
